import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const INITIAL_FORM = {
  tenantPublicId: '',
  organizationId: '',
};

function EsetMappingConfig() {
  const [tenants, setTenants] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [syncingTenants, setSyncingTenants] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tenantResponse, mappingResponse, organizationResponse] = await Promise.all([
        api.get('/api/eset/tenants'),
        api.get('/api/eset/mappings'),
        api.get('/api/eset/mapping-organizations'),
      ]);
      setTenants(Array.isArray(tenantResponse.data) ? tenantResponse.data : []);
      setMappings(Array.isArray(mappingResponse.data) ? mappingResponse.data : []);
      setOrganizations(Array.isArray(organizationResponse.data) ? organizationResponse.data : []);
    } catch (error) {
      setFeedback({ severity: 'error', text: error?.response?.data?.error || 'Impossible de charger les tenants ESET.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantMappings = async () => {
    const [tenantResponse, mappingResponse] = await Promise.all([
      api.get('/api/eset/tenants'),
      api.get('/api/eset/mappings'),
    ]);
    setTenants(Array.isArray(tenantResponse.data) ? tenantResponse.data : []);
    setMappings(Array.isArray(mappingResponse.data) ? mappingResponse.data : []);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const availableTenants = useMemo(() => {
    const selected = form.tenantPublicId;
    return tenants.filter((tenant) => !tenant.mapping || tenant.tenantPublicId === selected);
  }, [tenants, form.tenantPublicId]);

  const activeOrganizations = useMemo(
    () => organizations.filter((organization) => !organization.deleted),
    [organizations]
  );

  const selectedTenant = availableTenants.find((tenant) => tenant.tenantPublicId === form.tenantPublicId) || null;
  const selectedOrganization = activeOrganizations.find((organization) => organization._id === form.organizationId) || null;

  const handleSave = async () => {
    if (!form.tenantPublicId || !form.organizationId) {
      setFeedback({ severity: 'warning', text: 'Selectionne un tenant ESET et un client.' });
      return;
    }
    try {
      setLoading(true);
      const { data: mapping } = await api.post('/api/eset/mappings', form);
      setMappings((current) => [
        ...current.filter((item) => item.tenantPublicId !== mapping.tenantPublicId),
        mapping,
      ]);
      setTenants((current) => current.map((tenant) => (
        tenant.tenantPublicId === mapping.tenantPublicId
          ? { ...tenant, mapping }
          : tenant
      )));
      setFeedback({ severity: 'success', text: 'Association ESET enregistrée.' });
      setForm(INITIAL_FORM);
    } catch (error) {
      setFeedback({ severity: 'error', text: error?.response?.data?.error || "Impossible d enregistrer l'association." });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (mappingId) => {
    try {
      await api.delete(`/api/eset/mappings/${mappingId}`);
      setMappings((current) => current.filter((mapping) => mapping._id !== mappingId));
      setTenants((current) => current.map((tenant) => (
        tenant.mapping?._id === mappingId
          ? { ...tenant, mapping: null }
          : tenant
      )));
      setFeedback({ severity: 'success', text: 'Association retiree.' });
    } catch (error) {
      setFeedback({ severity: 'error', text: error?.response?.data?.error || 'Impossible de retirer l association.' });
    }
  };

  const handleSyncTenants = async () => {
    try {
      setSyncingTenants(true);
      const { data } = await api.post('/api/eset/tenants/sync');
      await fetchTenantMappings();
      setFeedback({
        severity: 'success',
        text: `Catalogue ESET actualise : ${data?.tenants || 0} tenants disponibles.`,
      });
    } catch (error) {
      setFeedback({ severity: 'error', text: error?.response?.data?.error || 'La synchronisation des tenants ESET a echoue.' });
    } finally {
      setSyncingTenants(false);
    }
  };

  const mappingColumns = useMemo(
    () => [
      {
        accessorFn: (mapping) => mapping.Organization?.name || 'Client supprime',
        id: 'organizationName',
        header: 'Client',
        size: 220,
        filterVariant: 'autocomplete',
        filterFn: 'equals',
      },
      {
        accessorKey: 'tenantName',
        header: 'Tenant ESET',
        size: 260,
        filterVariant: 'autocomplete',
      },
      {
        accessorKey: 'tenantPublicId',
        header: 'Identifiant ESET',
        size: 280,
      },
      {
        accessorFn: (mapping) => (mapping.enabled ? 'Actif' : 'Inactif'),
        id: 'statusLabel',
        header: 'Statut',
        size: 100,
        filterVariant: 'autocomplete',
        Cell: ({ row }) => (
          <Chip
            size="small"
            color={row.original.enabled ? 'success' : 'default'}
            label={row.original.enabled ? 'Actif' : 'Inactif'}
            variant="outlined"
          />
        ),
      },
    ],
    []
  );

  const mappingTable = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,

    columns: mappingColumns,
    data: mappings,
    state: { showGlobalFilter: true, showColumnFilters: true,  isLoading: loading },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,

      density: 'compact',
      grouping: ['organizationName'],
      expanded: {},
      sorting: [
        { id: 'organizationName', desc: false },
        { id: 'tenantName', desc: false },
      ],
      pagination: { pageSize: 25 },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      placeholder: 'Rechercher un mapping',
      variant: 'outlined',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderRowActions: ({ row }) => (
      <Button
        size="small"
        color="error"
        startIcon={<DeleteOutlineIcon />}
        onClick={() => handleDelete(row.original._id)}
      >
        Retirer
      </Button>
    ),
    renderTopToolbarCustomActions: () => (
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        label={`${mappings.length} association${mappings.length > 1 ? 's' : ''}`}
      />
    ),
  });

  return (
    <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          mb={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>Configuration</Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            variant="contained"
            onClick={handleSyncTenants}
            disabled={syncingTenants}
          >
            {syncingTenants ? 'Synchronisation...' : 'Resynchroniser les tenants'}
          </Button>
        </Stack>

        {feedback ? <Alert severity={feedback.severity} sx={{ mb: 2 }}>{feedback.text}</Alert> : null}
        {!loading && !tenants.length ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Aucun tenant en base. Lance une premiere resynchronisation des tenants ESET.
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{xs:12, md:5}}>
            <Autocomplete
              options={availableTenants}
              value={selectedTenant}
              getOptionLabel={(tenant) => tenant.tenantName || ''}
              isOptionEqualToValue={(option, value) => option.tenantPublicId === value.tenantPublicId}
              onChange={(_, tenant) => setForm((current) => ({
                ...current,
                tenantPublicId: tenant?.tenantPublicId || '',
              }))}
              disabled={loading}
              noOptionsText="Aucun tenant disponible"
              renderInput={(params) => (
                <TextField {...params} label="Tenant ESET" placeholder="Rechercher un tenant" />
              )}
            />
          </Grid>
          <Grid size={{xs:12, md:5}}>
            <Autocomplete
              options={activeOrganizations}
              value={selectedOrganization}
              getOptionLabel={(organization) => organization.name || ''}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(_, organization) => setForm((current) => ({
                ...current,
                organizationId: organization?._id || '',
              }))}
              disabled={loading}
              noOptionsText="Aucun client disponible"
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Rechercher un client" />
              )}
            />
          </Grid>
          <Grid size={{xs:12, md:2}}>
            <Button fullWidth variant="contained" onClick={handleSave} disabled={loading} sx={{ height: '100%' }}>
              Associer
            </Button>
          </Grid>
        </Grid>

        <MaterialReactTable table={mappingTable} />
      </CardContent>
    </Card>
  );
}

export default EsetMappingConfig;
